/*
  About logos marquee (sections/about-logos.liquid).

  Plain JS, no carousel library. The logos are cloned until one run is wider
  than the viewport, then the track moves left at a constant speed and jumps
  back by exactly one run, so the loop has no seam. Scrolling pauses while a
  mouse hovers the strip and while the strip is off-screen.
*/
(function () {
  const PIXELS_PER_SECOND_PER_STEP = 12;

  function initLogos(marquee) {
    if (marquee.dataset.logosInitialized === 'true') return;

    const track = marquee.querySelector('[data-about-logos-track]');
    const viewport = marquee.querySelector('.about-logos__viewport');
    if (!track || !viewport || track.children.length === 0) return;

    marquee.dataset.logosInitialized = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (marquee.dataset.autoplay !== 'true') return;
    if (reducedMotion.matches) {
      marquee.classList.add('is-static');
      return;
    }

    const originals = [...track.children];
    const speed = (parseFloat(marquee.dataset.speed) || 3) * PIXELS_PER_SECOND_PER_STEP;

    let runWidth = 0;
    let offset = 0;
    let hovering = false;
    let visible = true;
    let frameId = 0;
    let lastTime = 0;

    // Width of one full run of logos, including the trailing gap.
    const measure = () => {
      runWidth = originals.reduce((sum, slide) => sum + slide.getBoundingClientRect().width, 0);
    };

    const fill = () => {
      track.querySelectorAll('[data-logos-clone]').forEach((clone) => clone.remove());
      measure();
      if (!runWidth) return;

      // One extra run past the viewport is enough for a seamless wrap.
      const copies = Math.ceil(viewport.offsetWidth / runWidth) + 1;
      for (let i = 0; i < copies; i += 1) {
        originals.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-logos-clone', '');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          track.appendChild(clone);
        });
      }
    };

    const step = (time) => {
      frameId = 0;
      if (hovering || !visible) return;

      // Clamp the delta so a background tab does not cause a big jump.
      const delta = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;

      offset += speed * delta;
      if (runWidth && offset >= runWidth) offset -= runWidth;
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      frameId = requestAnimationFrame(step);
    };

    const play = () => {
      if (frameId || hovering || !visible) return;
      lastTime = performance.now();
      frameId = requestAnimationFrame(step);
    };

    const pause = () => {
      cancelAnimationFrame(frameId);
      frameId = 0;
    };

    fill();

    // Mouse only: touch pointers fire "enter" on tap and never "leave".
    marquee.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      pause();
    });
    marquee.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = false;
      play();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        if (visible) play();
        else pause();
      }).observe(marquee);
    }

    // Logos load lazily, so widths change after the first measure.
    let resizeTimer = 0;
    const refresh = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        fill();
        if (runWidth) offset %= runWidth;
      }, 150);
    };
    window.addEventListener('resize', refresh);
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', refresh, { once: true });
    });

    play();
  }

  function initAll(root) {
    root.querySelectorAll('[data-about-logos]').forEach(initLogos);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  // The theme editor swaps in fresh markup when the section's settings change.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
